import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormGroup,
  FormControlLabel,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Label as TagIcon,
} from '@mui/icons-material';
import { TeamMember, User } from '../../types';
import { getFirstName } from '../../utils/sortByFirstName';
import { teamService } from '../../services/teamService';
import { userService } from '../../services/userService';

interface AthleteTagManagerProps {
  open: boolean;
  onClose: () => void;
  teamId: string;
  onTagsUpdated: () => void;
}

const AVAILABLE_TAGS = [
  { value: 'cross-country', label: 'Cross Country', color: '#4CAF50' },
  { value: '400m', label: '400m', color: '#FF9800' },
  { value: '800m', label: '800m', color: '#FF5722' },
  { value: '1600m', label: '1600m (Mile)', color: '#F44336' },
  { value: '3200m', label: '3200m (2 Mile)', color: '#E91E63' },
  { value: '3k', label: '3k', color: '#9C27B0' },
  { value: '5K', label: '5K', color: '#673AB7' },
  { value: '10K', label: '10K', color: '#3F51B5' },
  { value: 'Half Marathon', label: 'Half Marathon', color: '#2196F3' },
  { value: 'Marathon', label: 'Marathon', color: '#00BCD4' },
];

interface AthleteWithTags {
  member: TeamMember;
  user: User;
  tags: string[];
}

const AthleteTagManager: React.FC<AthleteTagManagerProps> = ({
  open,
  onClose,
  teamId,
  onTagsUpdated,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [athletes, setAthletes] = useState<AthleteWithTags[]>([]);

  useEffect(() => {
    if (open) {
      loadAthletes();
    }
  }, [open, teamId]);

  const loadAthletes = async () => {
    setLoading(true);
    setError('');
    try {
      const members = await teamService.getTeamMembers(teamId);
      
      const athletesData: AthleteWithTags[] = [];
      for (const member of members) {
        const user = await userService.getUserById(member.athleteId);
        if (user) {
          athletesData.push({
            member,
            user,
            tags: member.tags || [],
          });
        }
      }
      athletesData.sort((a, b) =>
        getFirstName(a.user.displayName).localeCompare(getFirstName(b.user.displayName), undefined, { sensitivity: 'base' })
      );
      setAthletes(athletesData);
    } catch (err: any) {
      setError('Failed to load athletes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTag = (athleteIndex: number, tag: string) => {
    setAthletes(prev => {
      const updated = [...prev];
      const athlete = updated[athleteIndex];
      const currentTags = athlete.tags || [];
      
      if (currentTags.includes(tag)) {
        athlete.tags = currentTags.filter(t => t !== tag);
      } else {
        athlete.tags = [...currentTags, tag];
      }
      
      return updated;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      // Update tags for each athlete
      for (const athlete of athletes) {
        await teamService.updateMemberTags(athlete.member.id, athlete.tags);
      }
      
      setSuccess('Tags updated successfully!');
      onTagsUpdated();
      
      // Close after short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError('Failed to save tags');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const getTagColor = (tagValue: string) => {
    const tag = AVAILABLE_TAGS.find(t => t.value === tagValue);
    return tag?.color || '#999';
  };

  const getTagLabel = (tagValue: string) => {
    const tag = AVAILABLE_TAGS.find(t => t.value === tagValue);
    return tag?.label || tagValue;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TagIcon />
          Manage Athlete Tags
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Tag athletes by event or season. This helps auto-select participants when entering race results.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : athletes.length === 0 ? (
          <Alert severity="info">No athletes found in this team</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Athlete</TableCell>
                  <TableCell>Current Tags</TableCell>
                  <TableCell>Add/Remove Tags</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {athletes.map((athlete, index) => (
                  <TableRow key={athlete.member.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {athlete.user.displayName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {athlete.tags.length > 0 ? (
                          athlete.tags.map(tag => (
                            <Chip
                              key={tag}
                              label={getTagLabel(tag)}
                              size="small"
                              sx={{
                                backgroundColor: getTagColor(tag),
                                color: '#fff',
                                fontWeight: 'bold',
                              }}
                            />
                          ))
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No tags
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {AVAILABLE_TAGS.map(tag => (
                          <FormControlLabel
                            key={tag.value}
                            control={
                              <Checkbox
                                checked={athlete.tags.includes(tag.value)}
                                onChange={() => handleToggleTag(index, tag.value)}
                                size="small"
                              />
                            }
                            label={
                              <Typography variant="caption">
                                {tag.label}
                              </Typography>
                            }
                            sx={{ m: 0, mr: 1 }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Saving...' : 'Save Tags'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AthleteTagManager;

